/**
 * Square Form Transformers
 *
 * Transforms between API response format and form values for Squares contests
 */

import { normalizeTeamLogoUrl } from '@/utils/normalizeTeamLogoUrl';

export type SquareFormValues = {
  tenant_id: string;
  contest_name_en: string;
  contest_name_fr?: string;
  contest_subtext_en?: string;
  contest_subtext_fr?: string;
  lobby_tag_input?: string;
  lobby_tags?: string[];
  status?: string;
  entry_fee?: number;
  max_squares_per_user?: number;
  start_time_utc?: string;
  open_time_utc?: string;
  selection_editable_until_utc?: string;
  row_team_id?: string;
  col_team_id?: string;
  row_team_display_name?: string;
  col_team_display_name?: string;
  row_team_abbreviation?: string;
  col_team_abbreviation?: string;
  row_team_bg_color?: string;
  col_team_bg_color?: string;
  row_team_icon_url?: string;
  col_team_icon_url?: string;
  auto_team_assets_from_matchup?: boolean;
  /** League IP (Intellectual Property) flag — team logos when on, team avatars when off */
  league_ip?: boolean;
  /** When true, contest is hidden from the Lobby and only reachable via direct URL */
  lobby_hidden?: boolean;
  /**
   * Reserve a Seat (DKPLAT-529) — when on, users can reserve a seat before the
   * contest opens for selections (requires a Questions Posted At time on the
   * linked pickem). Shared infra with Pick'em (DKPLAT-528).
   */
  reserve_a_seat_enabled?: boolean;
  /**
   * When questions post for the linked pickem — the reserve window runs until
   * this time. Required (in the future) for the reserve flow to trigger.
   */
  questions_posted_at?: string;
  sport: string;
  fixture_id?: string;
  scheduled_game_start_utc?: string;
  feed_provider?: string;
  grid_rows?: number;
  grid_cols?: number;
  periods?: any[];
  winner_rule?: string;
  winner_rule_config?: any;
  payout_distribution_method?: string;
  total_prize_amount?: number;
  prize_text_en?: string;
  prize_text_fr?: string;
  gam_enabled?: boolean;
  google_manager_sdk_enabled?: boolean;
  ad_units?: any[];
  sticky_footer_placement?: any; // GoogleAdsPlacement for STICKY_FOOTER (sizes, targeting s1/s2/s3/pid/pos)
  badge_image_url?: string;
  background_image_url?: string;
  rules_image_url?: string;
  submit_view_image_url?: string;
  game_rules_version_id?: string;
};

export type SquareData = {
  squares_contest_id: string;
  question_id?: string; // For new question-based system
  pickem_id?: string; // For new question-based system
  tenant_id: string;
  contest_name_en?: string;
  contest_name_fr?: string;
  contest_subtext_en?: string;
  contest_subtext_fr?: string;
  lobby_tags?: string[] | null;
  status?: string;
  entry_fee?: number;
  max_squares_per_user?: number;
  start_time_utc?: string;
  selection_editable_until_utc?: string;
  row_team_id?: string;
  col_team_id?: string;
  row_team_display_name?: string;
  col_team_display_name?: string;
  row_team_abbreviation?: string;
  col_team_abbreviation?: string;
  row_team_bg_color?: string;
  col_team_bg_color?: string;
  row_team_icon_url?: string;
  col_team_icon_url?: string;
  league_ip?: boolean;
  lobby_hidden?: boolean;
  reserve_a_seat_enabled?: boolean;
  questions_posted_at?: string | null;
  sport?: string;
  fixture_id?: string;
  scheduled_game_start_utc?: string;
  feed_provider?: string;
  grid_rows?: number;
  grid_cols?: number;
  periods?: any[];
  winner_rule?: string;
  winner_rule_config?: any;
  payout_distribution_method?: string;
  total_prize_amount?: number;
  gam_enabled?: boolean;
  google_manager_sdk_enabled?: boolean;
  ad_units?: any[];
  sticky_footer_placement?: any;
  editable?: boolean;
  edit_message?: string;
  game_rules_version_id?: string;
};

const normalizeAssetUrl = (url?: string | null): string | undefined =>
  url ? normalizeTeamLogoUrl(url) : undefined;

/**
 * Convert API response to form values
 */
export function apiToFormValues(
  square?: SquareData | null,
  searchLeagueId?: string
): SquareFormValues {
  if (!square) {
    // Default "Open Date & Time" to current time for new contests
    const currentTime = new Date().toISOString();
    return {
      tenant_id: searchLeagueId || '',
      contest_name_en: '',
      contest_name_fr: '',
      contest_subtext_en: '',
      contest_subtext_fr: '',
      lobby_tag_input: '',
      lobby_tags: [],
      status: 'draft',
      entry_fee: 0,
      max_squares_per_user: 1,
      sport: 'NCAAMB',
      start_time_utc: '',
      open_time_utc: '',
      selection_editable_until_utc: currentTime,
      row_team_id: '',
      col_team_id: '',
      row_team_display_name: '',
      col_team_display_name: '',
      row_team_abbreviation: '',
      col_team_abbreviation: '',
      row_team_bg_color: '',
      col_team_bg_color: '',
      row_team_icon_url: '',
      col_team_icon_url: '',
      auto_team_assets_from_matchup: true,
      league_ip: false,
      lobby_hidden: false,
      reserve_a_seat_enabled: false,
      questions_posted_at: '',
      fixture_id: '',
      scheduled_game_start_utc: '',
      feed_provider: '',
      grid_rows: 10,
      grid_cols: 10,
      periods: [],
      winner_rule: 'last_digit_of_score',
      winner_rule_config: {},
      payout_distribution_method: 'split_among_winners',
      total_prize_amount: 0,
      prize_text_en: '',
      prize_text_fr: '',
      gam_enabled: true,
      google_manager_sdk_enabled: true,
      ad_units: [],
      sticky_footer_placement: null,
      badge_image_url: '',
      background_image_url: '',
      rules_image_url: '',
      submit_view_image_url: '',
      game_rules_version_id: '',
    };
  }

  return {
    tenant_id: square.tenant_id || searchLeagueId || '',
    contest_name_en: square.contest_name_en || '',
    contest_name_fr: square.contest_name_fr,
    contest_subtext_en: square.contest_subtext_en,
    contest_subtext_fr: square.contest_subtext_fr,
    lobby_tag_input: '',
    lobby_tags: square.lobby_tags ?? [],
    status: square.status || 'draft',
    entry_fee: square.entry_fee ?? 0,
    max_squares_per_user: square.max_squares_per_user ?? 1,
    start_time_utc: square.start_time_utc,
    selection_editable_until_utc: square.selection_editable_until_utc,
    row_team_id: square.row_team_id,
    col_team_id: square.col_team_id,
    row_team_display_name: square.row_team_display_name,
    col_team_display_name: square.col_team_display_name,
    row_team_abbreviation: (square as any).row_team_abbreviation,
    col_team_abbreviation: (square as any).col_team_abbreviation,
    row_team_bg_color: square.row_team_bg_color,
    col_team_bg_color: square.col_team_bg_color,
    row_team_icon_url: normalizeAssetUrl(square.row_team_icon_url),
    col_team_icon_url: normalizeAssetUrl(square.col_team_icon_url),
    auto_team_assets_from_matchup: true,
    league_ip: square.league_ip ?? false,
    lobby_hidden: square.lobby_hidden ?? false,
    reserve_a_seat_enabled: square.reserve_a_seat_enabled ?? false,
    questions_posted_at: square.questions_posted_at || '',
    sport: square.sport || 'NCAAMB',
    fixture_id: square.fixture_id,
    scheduled_game_start_utc: square.scheduled_game_start_utc,
    feed_provider: square.feed_provider,
    grid_rows: square.grid_rows ?? 10,
    grid_cols: square.grid_cols ?? 10,
    periods: square.periods,
    winner_rule: square.winner_rule || 'last_digit_of_score',
    winner_rule_config: square.winner_rule_config,
    payout_distribution_method: square.payout_distribution_method || 'split_among_winners',
    total_prize_amount: square.total_prize_amount ?? 0,
    gam_enabled: square.gam_enabled !== false,
    google_manager_sdk_enabled: square.google_manager_sdk_enabled !== false,
    ad_units: square.ad_units || [],
    sticky_footer_placement: (square as any).sticky_footer_placement ?? null,
    badge_image_url: (square as any).badge_image_url,
    background_image_url: (square as any).background_image_url,
    rules_image_url: (square as any).rules_image_url,
    submit_view_image_url: (square as any).submit_view_image_url,
    game_rules_version_id: (square as any).game_rules_version_id || '',
  };
}

/**
 * Convert form values to create payload
 */
export function formValuesToCreatePayload(values: SquareFormValues): any {
  return {
    tenant_id: values.tenant_id,
    contest_name_en: values.contest_name_en,
    contest_name_fr: values.contest_name_fr,
    contest_subtext_en: values.contest_subtext_en,
    contest_subtext_fr: values.contest_subtext_fr,
    lobby_tags: values.lobby_tags && values.lobby_tags.length > 0 ? values.lobby_tags : null,
    status: values.status || 'DRAFT',
    entry_fee: values.entry_fee ?? 0,
    max_squares_per_user: values.max_squares_per_user ?? 1,
    start_time_utc: values.start_time_utc,
    selection_editable_until_utc: values.selection_editable_until_utc || values.start_time_utc,
    row_team_id: values.row_team_id,
    col_team_id: values.col_team_id,
    row_team_display_name: values.row_team_display_name,
    col_team_display_name: values.col_team_display_name,
    row_team_abbreviation: values.row_team_abbreviation,
    col_team_abbreviation: values.col_team_abbreviation,
    row_team_bg_color: values.row_team_bg_color,
    col_team_bg_color: values.col_team_bg_color,
    row_team_icon_url: values.row_team_icon_url,
    col_team_icon_url: values.col_team_icon_url,
    league_ip: values.league_ip === true,
    lobby_hidden: values.lobby_hidden === true,
    reserve_a_seat_enabled: values.reserve_a_seat_enabled === true,
    questions_posted_at: values.questions_posted_at || null,
    sport: values.sport,
    fixture_id: values.fixture_id,
    scheduled_game_start_utc: values.scheduled_game_start_utc,
    feed_provider: values.feed_provider,
    grid_rows: values.grid_rows ?? 10,
    grid_cols: values.grid_cols ?? 10,
    periods: values.periods,
    winner_rule: values.winner_rule || 'last_digit_of_score',
    winner_rule_config: values.winner_rule_config,
    payout_distribution_method: values.payout_distribution_method || 'split_among_winners',
    total_prize_amount: values.total_prize_amount ?? 0,
    gam_enabled: values.gam_enabled !== false,
    google_manager_sdk_enabled: values.google_manager_sdk_enabled !== false,
    ad_units: values.ad_units || [],
    sticky_footer_placement: values.sticky_footer_placement ?? null,
    badge_image_url: values.badge_image_url,
    background_image_url: values.background_image_url,
    rules_image_url: values.rules_image_url,
    submit_view_image_url: values.submit_view_image_url,
    prize_text_en: values.prize_text_en,
    prize_text_fr: values.prize_text_fr,
    game_rules_version_id: values.game_rules_version_id || undefined,
  };
}

/**
 * Convert form values to update payload
 */
export function formValuesToUpdatePayload(squareId: string, values: SquareFormValues): any {
  return {
    id: squareId,
    tenant_id: values.tenant_id,
    contest_name_en: values.contest_name_en,
    contest_name_fr: values.contest_name_fr,
    contest_subtext_en: values.contest_subtext_en,
    contest_subtext_fr: values.contest_subtext_fr,
    lobby_tags: values.lobby_tags && values.lobby_tags.length > 0 ? values.lobby_tags : null,
    status: values.status,
    entry_fee: values.entry_fee,
    max_squares_per_user: values.max_squares_per_user,
    start_time_utc: values.start_time_utc,
    // Send Open Date & Time as set by user (no fallback so it persists on save; '' clears on backend)
    selection_editable_until_utc: values.selection_editable_until_utc,
    row_team_id: values.row_team_id,
    col_team_id: values.col_team_id,
    row_team_display_name: values.row_team_display_name,
    col_team_display_name: values.col_team_display_name,
    row_team_abbreviation: values.row_team_abbreviation,
    col_team_abbreviation: values.col_team_abbreviation,
    row_team_bg_color: values.row_team_bg_color,
    col_team_bg_color: values.col_team_bg_color,
    row_team_icon_url: values.row_team_icon_url,
    col_team_icon_url: values.col_team_icon_url,
    league_ip: values.league_ip === true,
    lobby_hidden: values.lobby_hidden === true,
    reserve_a_seat_enabled: values.reserve_a_seat_enabled === true,
    questions_posted_at: values.questions_posted_at || null,
    sport: values.sport,
    fixture_id: values.fixture_id,
    scheduled_game_start_utc: values.scheduled_game_start_utc,
    feed_provider: values.feed_provider,
    grid_rows: values.grid_rows,
    grid_cols: values.grid_cols,
    periods: values.periods,
    winner_rule: values.winner_rule,
    winner_rule_config: values.winner_rule_config,
    payout_distribution_method: values.payout_distribution_method,
    total_prize_amount: values.total_prize_amount,
    gam_enabled: values.gam_enabled,
    google_manager_sdk_enabled: values.google_manager_sdk_enabled,
    ad_units: values.ad_units,
    sticky_footer_placement: values.sticky_footer_placement ?? null,
    badge_image_url: values.badge_image_url,
    background_image_url: values.background_image_url,
    rules_image_url: values.rules_image_url,
    submit_view_image_url: values.submit_view_image_url,
    prize_text_en: values.prize_text_en,
    prize_text_fr: values.prize_text_fr,
    game_rules_version_id: values.game_rules_version_id || undefined,
  };
}
